import { AtgChip } from "@/components/AtgChip";
import { PlayerName } from "@/components/PlayerCardDialog";
import React from "react";
import axios from "axios";
import { fmt, fmtPct } from "@/lib/format";
import { useTracker } from "@/lib/tracker";
import { Loader2, Target, Zap, Award, Gauge, Activity, AlertTriangle } from "lucide-react";

const POS = ["C", "LW", "RW", "D"];

const parseFinal = (final) => {
  if (!final || typeof final !== "string") return [null, null];
  const [a, b] = final.split("-").map((x) => parseInt(x, 10));
  return [Number.isNaN(a) ? null : a, Number.isNaN(b) ? null : b];
};

const StatChip = ({ label, value, accent = "text-slate-200", title }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-900/50 px-2 py-1.5" title={title}>
    <p className="font-mono text-[8px] uppercase tracking-widest text-slate-500">{label}</p>
    <p className={`mt-0.5 font-mono text-[13px] font-bold tabular-nums ${accent}`}>{value}</p>
  </div>
);

const Card = ({ icon: Icon, title, right, children, accent = "text-cyan-500", testId }) => (
  <div className="rounded-xl border border-slate-800 bg-slate-900/30 p-2.5" data-testid={testId}>
    <div className="mb-2 flex items-center justify-between gap-2 border-b border-slate-800 pb-1.5">
      <p className={`flex items-center gap-1.5 font-mono text-[10px] font-semibold uppercase tracking-widest ${accent}`}>
        {Icon && <Icon className="h-3 w-3" />} {title}
      </p>
      {right}
    </div>
    {children}
  </div>
);

const EnvBanner = ({ profile, game }) => {
  const away = game.away?.abbrev || "AWY";
  const home = game.home?.abbrev || "HOM";
  const chips = profile
    ? [
        ["Goal Environment", profile.pace, "text-cyan-300", `Combined GF/GP pace: ${profile.pace_value}`],
        ["Pace (GF/GP)", profile.pace_value, "text-cyan-200"],
        ["Shot Edge", profile.shot_edge, "text-amber-300"],
        ["Special Teams", profile.special_teams_edge, "text-emerald-300"],
        ["Defense Edge", profile.defense_edge, "text-rose-300"],
      ]
    : [];
  return (
    <div className="rounded-xl border border-cyan-900/40 bg-gradient-to-r from-cyan-950/20 to-slate-900/20 p-3" data-testid="game-trends-context">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 font-display text-[13px] font-bold uppercase tracking-wide text-cyan-300">
          <Gauge className="h-3.5 w-3.5" /> Current Game Context
        </p>
        <div className="flex flex-wrap items-center gap-2 font-mono text-[9px] uppercase tracking-wider text-slate-500">
          <span>{away} {game.away?.record || ""}</span>
          <span className="text-slate-700">@</span>
          <span>{home} {game.home?.record || ""}</span>
          {game.venue && <span className="text-slate-600">· {game.venue}</span>}
        </div>
      </div>
      {profile ? (
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-5">
          {chips.map(([l, v, c, t]) => (
            <StatChip key={l} label={l} value={v ?? "—"} accent={c} title={t} />
          ))}
        </div>
      ) : (
        <p className="py-2 text-center font-mono text-[10px] text-slate-600">Team stats unavailable — environment not computed</p>
      )}
    </div>
  );
};

const RANK_STYLE = ["bg-amber-400 text-slate-950", "bg-slate-300 text-slate-950", "bg-orange-700 text-white"];

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STAGES = [
  ["team", "Team perf"], ["weakness", "Pos weakness"], ["sog", "SOG allowed"],
  ["lineup", "Lineup slot"], ["origin", "Goal origin"], ["card", "Official card"],
];
const lastName = (n) => (n || "").split(" ").slice(-1)[0];

const useH2H = (goalie, picks) => {
  const [data, setData] = React.useState(null);
  const ids = [...new Set(picks.map((p) => p.player_id).filter(Boolean))].join(",");
  React.useEffect(() => {
    if (!goalie?.id || !ids) return;
    let live = true;
    axios.get(`${API}/h2h`, { params: { goalie_id: goalie.id, players: ids }, timeout: 120000 })
      .then((r) => live && setData(r.data)).catch(() => live && setData({ ok: false, players: {} }));
    return () => { live = false; };
  }, [goalie?.id, ids]);
  return data;
};

const H2HLabel = ({ h2h, pid, goalie, testId }) => {
  if (!goalie?.id) return null;
  if (!h2h) return <span className="font-mono text-[9px] text-slate-600" data-testid={testId}>H2H loading…</span>;
  const r = h2h.players?.[String(pid)];
  if (!r) return <span className="font-mono text-[9px] text-slate-600" data-testid={testId}>H2H n/a</span>;
  if (r.first_meeting) return <span className="rounded bg-slate-800 px-1 font-mono text-[9px] text-slate-400" data-testid={testId}>H2H: first meeting</span>;
  return (
    <span className="rounded bg-violet-950/60 px-1 font-mono text-[9px] font-semibold text-violet-200" data-testid={testId}
      title={`Career vs ${goalie.name}: ${r.gp} GP · ${r.g} G · ${r.a} A · ${r.pts} PTS · ${r.sog} SOG`}>
      H2H vs {lastName(goalie.name)}: {r.gp} GP · {r.g} G · {r.pts} PTS · {r.sog} SOG
    </span>
  );
};

const StageBars = ({ stages }) => (
  <div className="flex items-end gap-0.5" title={STAGES.map(([k, l]) => `${l}: ${Math.round(stages[k])}`).join("\n")}>
    {STAGES.map(([k]) => (
      <span key={k} className="w-1.5 rounded-sm bg-cyan-500/70" style={{ height: `${4 + (stages[k] / 100) * 12}px` }} />
    ))}
  </div>
);

const PickCard = ({ icon, title, subtitle, accent, players, scoreKey, teamAbbr, opponent, goalie, h2h, testId }) => {
  const { isTracked, toggle } = useTracker();
  return (
    <Card icon={icon} title={title} accent={accent} testId={testId}
      right={<span className="font-mono text-[8px] uppercase tracking-wider text-slate-600">{subtitle}</span>}>
      {players.length ? (
        <div className="space-y-1.5">
          {players.map((t, i) => {
            const trackObj = { ...t, name: t.player, line: t.slot, team: teamAbbr, opponent, opp_ga_gp: t.def_ga_gp, goalie_name: goalie?.name, score: t.cypher_score };
            const tracked = isTracked(trackObj);
            const rowId = t.jersey != null ? t.jersey : `${t.slot}-${i}`;
            return (
              <div key={`${rowId}-${i}`} className="rounded-lg border border-slate-800/70 bg-slate-900/40 px-2 py-1.5" data-testid={`${testId}-row-${rowId}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
                    <span className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full font-mono text-[9px] font-bold ${RANK_STYLE[i] || "bg-slate-700 text-slate-200"}`}>{i + 1}</span>
                    <span className="shrink-0 font-mono text-[11px] font-bold text-cyan-400">{t.jersey != null ? `#${t.jersey}` : "#—"}</span>
                    <PlayerName name={t.player} team={teamAbbr} className="font-display text-[12px] font-semibold uppercase leading-tight tracking-tight text-slate-100" />
                    <AtgChip atg={t.atg} testId={`trend-atg-${teamAbbr}-${t.jersey}`} />
                  </div>
                  <button data-testid={`${testId}-track-${rowId}`} onClick={() => toggle(trackObj)}
                    className={`shrink-0 rounded px-1 py-0.5 font-mono text-[9px] font-bold uppercase transition ${tracked ? "bg-lime-500 text-slate-950" : "border border-cyan-800 text-cyan-300 hover:bg-cyan-950"}`}>
                    {tracked ? "✓" : "+"}
                  </button>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5 pl-[22px]">
                  <span className="rounded bg-slate-800 px-1 font-mono text-[9px] font-semibold text-slate-300">{t.position}</span>
                  <span className="font-mono text-[9px] text-slate-500">{t.slot}</span>
                  {t.pp_unit && <span className="rounded bg-amber-950 px-1 font-mono text-[8px] font-bold text-amber-300">{t.pp_unit}</span>}
                  {t.card_status && <span className={`rounded px-1 font-mono text-[8px] font-bold ${t.card_status === "APPROVED" ? "bg-emerald-950 text-emerald-300" : "bg-sky-950 text-sky-300"}`}>{t.card_status === "APPROVED" ? "🟢" : "🔵"} {t.card_status}</span>}
                  <span className="font-mono text-[9px] text-slate-500" title="Line-origin goals vs tonight's goalie">ORIG {t.origin_goals}G/{t.origin_games}</span>
                  <span className="ml-auto flex items-center gap-1.5">
                    <StageBars stages={t.stages} />
                    <span className={`rounded bg-slate-800/80 px-1 font-mono text-[10px] font-bold tabular-nums ${accent}`} title="Funnel score">{t[scoreKey]}</span>
                  </span>
                </div>
                <div className="mt-1 pl-[22px]"><H2HLabel h2h={h2h} pid={t.player_id} goalie={goalie} testId={`${testId}-h2h-${rowId}`} /></div>
              </div>
            );
          })}
        </div>
      ) : (
        <p className="py-2 text-center font-mono text-[10px] text-slate-600">No qualified candidates</p>
      )}
    </Card>
  );
};

const Step = ({ n, title, children, testId }) => (
  <div className="relative rounded-lg border border-slate-800 bg-slate-950/50 px-2 py-1.5" data-testid={testId}>
    <p className="font-mono text-[8px] uppercase tracking-widest text-cyan-500"><span className="mr-1 rounded bg-cyan-950 px-1 text-cyan-300">{n}</span>{title}</p>
    <div className="mt-0.5 font-mono text-[10px] text-slate-300">{children}</div>
  </div>
);

const PosBars = ({ data, field, fmtv }) => (
  <div className="mt-0.5 grid grid-cols-4 gap-1">
    {POS.map((p) => (
      <div key={p} title={`${p}: ${data[p]?.[field] ?? "—"}`}>
        <div className="h-1 overflow-hidden rounded bg-slate-800"><div className="h-full bg-rose-500/80" style={{ width: `${data[p]?.score ?? 0}%` }} /></div>
        <p className="text-[8px] text-slate-500">{p} <span className="text-slate-300">{fmtv(data[p]?.[field])}</span></p>
      </div>
    ))}
  </div>
);

const DataFlow = ({ tr, testId }) => {
  const st = tr.stages;
  const topPos = POS.reduce((a, b) => (st.weakness[b].score > st.weakness[a].score ? b : a));
  const topSog = POS.reduce((a, b) => (st.sog[b].score > st.sog[a].score ? b : a));
  return (
    <div className="rounded-xl border border-cyan-900/30 bg-slate-900/30 p-2" data-testid={testId}>
      <p className="mb-1.5 font-mono text-[9px] uppercase tracking-widest text-slate-500">Data flow vs {tr.defender} → official card</p>
      <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
        <Step n="1" title="Team performance" testId={`${testId}-team`}>
          {tr.defender} GA/GP <b className="text-rose-300">{fmt(st.team.ga_gp)}</b> · L{st.team.l5_games} <b className="text-rose-300">{fmt(st.team.l5_ga_gp)}</b>
        </Step>
        <Step n="2" title="Weakness by position" testId={`${testId}-weakness`}>
          Weakest: <b className="text-rose-300">{topPos}</b>
          <PosBars data={st.weakness} field="season_ga" fmtv={(v) => (v == null ? "—" : fmt(v))} />
        </Step>
        <Step n="3" title="SOG allowed by position" testId={`${testId}-sog`}>
          Most: <b className="text-amber-300">{topSog}</b>
          <PosBars data={st.sog} field="sog_allowed" fmtv={(v) => (v == null ? "—" : fmt(v))} />
        </Step>
        <Step n="4" title="Lineup" testId={`${testId}-lineup`}>{tr.lineup_count} confirmed skaters slotted</Step>
        <Step n="5" title="Goal origin" testId={`${testId}-origin`}>vs <b className="text-cyan-300">{tr.goalie?.name || "TBD"}</b> <span className="text-slate-500">({tr.goalie?.status || "—"})</span></Step>
        <Step n="6" title="Official card" testId={`${testId}-card`}>{tr.card_count} on card (APPROVED / WATCH)</Step>
      </div>
    </div>
  );
};

const TeamPicks = ({ meta, tr, stats, hist, testId }) => {
  const abbr = meta?.abbrev || "—";
  const picks = tr?.status === "OK" ? [...tr.top_goal, ...tr.top_first, ...tr.top_multi] : [];
  const h2h = useH2H(tr?.goalie, picks);
  if (!tr || tr.status !== "OK") {
    return (
      <div className="space-y-2.5" data-testid={testId}>
        <TeamHeader meta={meta} hist={hist} />
        <div className="rounded-xl border border-amber-900/50 bg-amber-950/10 p-4 text-center">
          <AlertTriangle className="mx-auto mb-2 h-5 w-5 text-amber-500" />
          <p className="font-mono text-[11px] uppercase tracking-widest text-amber-400">Trends on Watch</p>
          <p className="mt-1 font-mono text-[11px] text-slate-400">{tr?.watch_reason || "Insufficient goal-origin / matchup data to project scorers."}</p>
        </div>
      </div>
    );
  }
  const common = { teamAbbr: abbr, opponent: tr.defender, goalie: tr.goalie, h2h };
  return (
    <div className="space-y-2.5" data-testid={testId}>
      <TeamHeader meta={meta} hist={hist} />
      {stats && (
        <div className="grid grid-cols-3 gap-1.5">
          <StatChip label="GF/GP" value={fmt(stats.gf_gp)} accent="text-lime-300" />
          <StatChip label="GA/GP" value={fmt(stats.ga_gp)} accent="text-rose-300" />
          <StatChip label="PP%" value={fmtPct(stats.pp_pct)} accent="text-emerald-300" />
        </div>
      )}
      <DataFlow tr={tr} testId={`${testId}-flow`} />
      <PickCard icon={Target} title="Top 3 Scorers" subtitle="full funnel score" accent="text-lime-300" players={tr.top_goal} scoreKey="funnel" {...common} testId={`${testId}-goal`} />
      <PickCard icon={Zap} title="Top 3 First-Goal Scorers" subtitle="funnel + first-goal history" accent="text-amber-300" players={tr.top_first} scoreKey="first_score" {...common} testId={`${testId}-first`} />
      <PickCard icon={Award} title="Top 3 Multi-Goal · Hat Trick" subtitle="funnel + multi-goal history" accent="text-rose-300" players={tr.top_multi} scoreKey="multi_score" {...common} testId={`${testId}-multi`} />
    </div>
  );
};

const TeamHeader = ({ meta, hist }) => {
  const log = hist?.ok ? hist.game_log || [] : [];
  let wins = 0, gf = 0, ga = 0, counted = 0;
  log.forEach((r) => {
    const [ts, os] = parseFinal(r.final);
    if (ts != null && os != null) { gf += ts; ga += os; counted += 1; }
    if (r.result === "W") wins += 1;
  });
  return (
    <div className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-900/50 px-3 py-2">
      <div className="flex items-center gap-2">
        {meta?.logo && <img src={meta.logo} alt="" className="h-6 w-6 object-contain" />}
        <span className="font-display text-base font-extrabold uppercase tracking-tight text-white">{meta?.abbrev || "—"}</span>
        {meta?.record && <span className="font-mono text-[10px] text-slate-500">{meta.record}</span>}
      </div>
      {log.length > 0 && (
        <span className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-wider text-slate-400">
          <Activity className="h-3 w-3 text-cyan-500" />
          L{log.length}: <span className="text-emerald-400">{wins}W</span>-<span className="text-rose-400">{log.length - wins}L</span>
          {counted > 0 && <span className="text-slate-500">· {(gf / counted).toFixed(1)}GF / {(ga / counted).toFixed(1)}GA</span>}
        </span>
      )}
    </div>
  );
};

export const GameTrendsTab = ({ analysis, history, game, loading, error, onRetry }) => {
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin text-cyan-500" />
        <p className="mt-3 font-mono text-[11px] uppercase tracking-widest">Compiling game trends…</p>
      </div>
    );
  }
  if (error || !analysis) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <p className="font-mono text-[11px] uppercase tracking-widest text-rose-400">Game trends unavailable</p>
        <button onClick={onRetry} className="mt-3 rounded bg-slate-800 px-3 py-1 font-mono text-[11px] text-slate-300 hover:bg-slate-700">Retry</button>
      </div>
    );
  }

  const tr = analysis.trends || {};
  return (
    <div className="space-y-3" data-testid={`game-trends-${game.away?.abbrev}-${game.home?.abbrev}`}>
      <EnvBanner profile={analysis.game_profile} game={game} />
      <p className="font-mono text-[9px] uppercase tracking-wider text-slate-600">
        Flow: team performance → weakness by position → SOG allowed by position → lineup → goal origin → official card.
      </p>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <TeamPicks meta={game.away} tr={tr.away} stats={game.away_stats} hist={history?.away} testId={`game-trends-col-${game.away?.abbrev}`} />
        <TeamPicks meta={game.home} tr={tr.home} stats={game.home_stats} hist={history?.home} testId={`game-trends-col-${game.home?.abbrev}`} />
      </div>
    </div>
  );
};

export default GameTrendsTab;
